<script lang="ts" setup>
import type { Category } from "~/models/Category"

/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    categories: Category[]
    dropdownText: string
    loading?: boolean
    loadingText?: string
    currentSelectedCategory?: string | null
}>()

const emit = defineEmits<{
    (e: "selectCategory", category: Category, parentCategory?: Category): void
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const openDropdown = ref(false)
const selectedCategories = ref<{
    parent: Category | null
    child: Category | null
}>({
    parent: null,
    child: null,
})

const expandedCategories = ref<Set<string>>(new Set())

const buttonRef = ref<HTMLButtonElement | null>(null)
const menuPosition = ref({ top: 0, left: 0, width: 0 })

/**
 *
 *  Life cicle events
 *
 *
 */

onMounted(() => {
    document.addEventListener("click", closeDropdownOnClickOutside)
    window.addEventListener("resize", updateMenuPosition)
    window.addEventListener("scroll", updateMenuPosition, true)
})

onUnmounted(() => {
    document.removeEventListener("click", closeDropdownOnClickOutside)
    window.removeEventListener("resize", updateMenuPosition)
    window.removeEventListener("scroll", updateMenuPosition, true)
})

/**
 *
 *  Functions section
 *
 *
 */

function handleCategorySelect(category: Category, parentCategory?: Category) {
    if (parentCategory) {
        selectedCategories.value = {
            parent: parentCategory,
            child: category,
        }
    } else {
        selectedCategories.value = {
            parent: category,
            child: null,
        }
    }

    emit("selectCategory", category, parentCategory)

    openDropdown.value = false
}

function toggleSubcategory(event: Event, category: Category) {
    event.stopPropagation()

    const categoryId = `cat-${category.id || category.label}`

    if (expandedCategories.value.has(categoryId)) {
        expandedCategories.value.delete(categoryId)
    } else {
        expandedCategories.value.add(categoryId)
    }
}

const isExpanded = (category: Category) => {
    const categoryId = `cat-${category.id || category.label}`
    return expandedCategories.value.has(categoryId)
}

const getCategoryId = (category: Category) => {
    return `cat-${category.id || category.label}`
}

const toggleOpenDropdown = () => {
    if (props.loading) return

    if (!openDropdown.value) {
        expandedCategories.value.clear()
        updateMenuPosition()
    }

    openDropdown.value = !openDropdown.value
}

const updateMenuPosition = () => {
    if (!buttonRef.value) return
    const rect = buttonRef.value.getBoundingClientRect()
    menuPosition.value = {
        top: rect.bottom + 10,
        left: rect.left,
        width: rect.width,
    }
}

const isCategorySelected = (category: Category) => {
    if (
        category.label === props.currentSelectedCategory ||
        selectedCategories.value.parent?.id === category.id ||
        selectedCategories.value.child?.id === category.id
    ) {
        return "tw-text-primary"
    }

    return ""
}

const closeDropdownOnClickOutside = (event: MouseEvent) => {
    const dropdown = document.getElementById("custom-categories-dropdown")
    const dropdownButton = document.getElementById("custom-categories-button")

    if (
        dropdown &&
        !dropdown.contains(event.target as Node) &&
        dropdownButton &&
        !dropdownButton.contains(event.target as Node)
    ) {
        openDropdown.value = false

        expandedCategories.value.clear()
    }
}
</script>

<template>
    <div class="custom-dropdown-container tw-w-full tw-max-w-[385px]">
        <button
            id="custom-categories-button"
            ref="buttonRef"
            class="custom-dropdown-button tw-text-white tw-font-titilium tw-capitalize tw-px-5 tw-rounded-full tw-h-10 tw-text-base tw-font-bold tw-z-50 !tw-border-0 tw-bg-primary tw-w-full"
            @click="toggleOpenDropdown"
        >
            <div
                class="tw-flex tw-gap-2 tw-justify-between tw-items-center tw-w-full"
            >
                <span class="tw-px-auto">{{ dropdownText }}</span>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <div
                        v-if="loading"
                        class="tw-cursor-pointer tw-z-40 all-pointer-events"
                    >
                        <Loader
                            size="20px"
                            color="white"
                            :tooltip="loadingText"
                        />
                    </div>
                    <template v-else>
                        <i
                            v-if="!openDropdown"
                            class="custom-menu-icon"
                            @click.stop="toggleOpenDropdown"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="20px"
                                height="20px"
                                fill="currentColor"
                            >
                                <path
                                    d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"
                                />
                            </svg>
                        </i>
                        <i
                            v-else
                            class="custom-close-icon"
                            @click.stop="toggleOpenDropdown"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="20px"
                                height="20px"
                                fill="currentColor"
                            >
                                <path
                                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                                />
                            </svg>
                        </i>
                    </template>
                </div>
            </div>
        </button>
        <ClientOnly>
            <Teleport to="body">
                <div
                    v-if="openDropdown"
                    id="custom-categories-dropdown"
                    class="custom-dropdown-menu tw-text-black tw-max-w-[385px]"
                    :style="{
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`,
                        width: `${menuPosition.width}px`,
                    }"
                >
                    <div class="custom-dropdown-menu-inner">
                        <ul
                            class="custom-category-list"
                            v-if="categories.length > 0"
                        >
                            <CustomDropdownItem
                                v-for="(category, index) in categories"
                                :key="index"
                                :category="category"
                                :parentPath="[]"
                                :isExpanded="isExpanded"
                                :getCategoryId="getCategoryId"
                                :isCategorySelected="isCategorySelected"
                                @selectCategory="handleCategorySelect"
                                @toggleSubcategory="toggleSubcategory"
                            />
                        </ul>
                        <div v-else class="tw-p-2">
                            <p>No hay categorías para mostrar</p>
                        </div>
                    </div>
                </div>
            </Teleport>
        </ClientOnly>
    </div>
</template>

<style scoped>
.custom-dropdown-container {
    position: relative;
}

.custom-dropdown-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.custom-dropdown-menu {
    position: fixed;
    min-width: 250px;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 9999;
    overflow: visible !important;
}

.custom-dropdown-menu-inner {
    padding: 4px 0;
}

.custom-category-list {
    list-style-type: none;
    padding: 0;
    margin: 0;
    width: 100%;
}

.custom-category-item,
.custom-category-item-parent {
    padding: 8px 12px;
    transition: background-color 0.2s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.custom-category-item:hover,
.custom-category-item-parent:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.custom-item-label {
    font-size: 0.875rem;
    line-height: 1.25rem;
}

@media (min-width: 1024px) {
    .custom-item-label {
        font-size: 1rem;
        line-height: 1.5rem;
    }
}

.custom-category-parent-container {
    position: relative;
    width: 100%;
}

.custom-expand-button {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.custom-expand-arrow {
    display: inline-flex;
    transition: transform 0.2s ease;
}

.custom-expand-arrow.rotated {
    transform: rotate(90deg);
}

.custom-subcategory-container {
    position: absolute;
    left: 100%;
    top: 0;
    min-width: 200px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 100;
    overflow: visible !important;
}

.custom-subcategory-list {
    list-style-type: none;
    padding: 4px 0;
    margin: 0;
    overflow: visible !important;
    width: 100%;
}

@media (max-width: 768px) {
    .custom-subcategory-container {
        position: static;
        left: auto;
        box-shadow: none;
        padding-left: 16px;
    }
}
</style>
